// @ts-nocheck
import { useEffect, useState } from 'react';

import { Text, XStack, YStack } from 'tamagui';

import { Header, Footer, SubscribeForm } from '../../components/landing';

const COMPARISON_ROWS = [
  { feature: 'Cross-app search', torch: 'Gmail, Slack, GitHub, Drive, and 50+ tools', notion: 'Notion pages only' },
  { feature: 'Kanban boards', torch: true, notion: 'Via databases (manual setup)' },
  { feature: 'Sprint management', torch: true, notion: false },
  { feature: 'Calendar', torch: 'Built-in (Google + Outlook sync)', notion: false },
  { feature: 'Documents', torch: true, notion: true },
  { feature: 'Wiki', torch: true, notion: true },
  { feature: 'OKR tracking', torch: true, notion: 'Via templates (manual)' },
  { feature: 'Announcements', torch: true, notion: false },
  { feature: 'Slack integration', torch: 'Full search + indexing', notion: 'Notifications only' },
  { feature: 'GitHub integration', torch: 'Full search + indexing', notion: 'Link previews only' },
  { feature: 'SSO / SAML', torch: 'Team plan', notion: 'Business plan ($18/user)' },
  { feature: 'Pricing', torch: 'Free / $14 Pro / $22 Team', notion: 'Free / $10 Plus / $18 Business' },
];

export default function TorchVsNotion() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderCell = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return (
        <Text fontSize={14} color={value ? '#ededed' : '$color6'}>
          {value ? '\u2713' : '\u2014'}
        </Text>
      );
    }
    return (
      <Text fontSize={14} color="$color11" lineHeight={20}>
        {value}
      </Text>
    );
  };

  return (
    <YStack backgroundColor="$background" minHeight="100vh">
      <Header />

      <YStack paddingTop={140} paddingHorizontal="$5" gap="$8" paddingBottom={80}>
        <YStack maxWidth={900} alignSelf="center" width="100%">
          {/* Page header */}
          <YStack alignItems="center" marginBottom={60}>
            <Text
              fontSize={13}
              fontWeight="600"
              color="#ededed"
              letterSpacing={2}
              textTransform="uppercase"
              marginBottom="$3"
            >
              Comparison
            </Text>
            <Text
              fontSize={isMobile ? 30 : 44}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-1.5}
              lineHeight={isMobile ? 36 : 52}
              color="$color12"
            >
              Torch vs Notion
            </Text>
            <Text
              fontSize={isMobile ? 16 : 18}
              color="$color9"
              textAlign="center"
              marginTop="$3"
              lineHeight={28}
              maxWidth={600}
            >
              Notion is a great docs and wiki tool. Torch is a unified workspace that searches across
              all your tools and includes built-in productivity apps.
            </Text>
          </YStack>

          {/* Comparison table */}
          <YStack
            borderRadius="$3"
            borderWidth={1}
            borderColor="$color4"
            overflow="hidden"
            marginBottom={60}
          >
            {/* Table header */}
            <XStack
              backgroundColor="$color2"
              paddingVertical="$3"
              paddingHorizontal="$4"
              borderBottomWidth={1}
              borderColor="$color4"
            >
              <div style={{ flex: 2 }}>
                <Text fontSize={13} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={1}>
                  Feature
                </Text>
              </div>
              <div style={{ flex: 2 }}>
                <Text fontSize={13} fontWeight="600" color="#ededed" textTransform="uppercase" letterSpacing={1}>
                  Torch
                </Text>
              </div>
              <div style={{ flex: 2 }}>
                <Text fontSize={13} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={1}>
                  Notion
                </Text>
              </div>
            </XStack>

            {COMPARISON_ROWS.map((row, i) => (
              <XStack
                key={row.feature}
                paddingVertical="$3"
                paddingHorizontal="$4"
                borderBottomWidth={i < COMPARISON_ROWS.length - 1 ? 1 : 0}
                borderColor="$color3"
                hoverStyle={{ backgroundColor: '$color2' }}
                flexDirection={isMobile ? 'column' : 'row'}
                gap={isMobile ? '$2' : undefined}
              >
                <div style={{ flex: 2 }}>
                  <Text fontSize={14} color="$color11" fontWeight="500">
                    {row.feature}
                  </Text>
                </div>
                <div style={{ flex: 2 }}>{renderCell(row.torch)}</div>
                <div style={{ flex: 2 }}>{renderCell(row.notion)}</div>
              </XStack>
            ))}
          </YStack>

          {/* When to choose sections */}
          <XStack
            gap="$5"
            flexDirection={isMobile ? 'column' : 'row'}
            marginBottom={60}
          >
            <YStack
              flex={1}
              padding="$5"
              borderRadius="$3"
              borderWidth={1}
              borderColor="$color4"
              gap="$3"
            >
              <Text fontSize={20} fontWeight="600" color="$color12">
                When to choose Notion
              </Text>
              <YStack gap="$2">
                {[
                  'You primarily need docs and wiki functionality.',
                  'You don\'t need cross-app search across multiple tools.',
                  'You prefer building custom workflows with databases.',
                  'Your team is already deeply invested in the Notion ecosystem.',
                ].map((point, i) => (
                  <XStack key={i} gap="$2" alignItems="flex-start">
                    <Text fontSize={14} color="$color7" style={{ marginTop: 2 } as any}>
                      {'\u2022'}
                    </Text>
                    <Text fontSize={14} color="$color10" lineHeight={22} flex={1}>
                      {point}
                    </Text>
                  </XStack>
                ))}
              </YStack>
            </YStack>

            <YStack
              flex={1}
              padding="$5"
              borderRadius="$3"
              borderWidth={2}
              borderColor="$color6"
              gap="$3"
              style={{ boxShadow: '0 0 30px rgba(255, 255, 255, 0.1)' }}
            >
              <Text fontSize={20} fontWeight="600" color="$color12">
                When to choose Torch
              </Text>
              <YStack gap="$2">
                {[
                  'You use 5+ tools and want one search bar to find anything.',
                  'You want Kanban, Calendar, Docs, Wiki, and OKRs in one place.',
                  'You work across Microsoft, Google, and Slack ecosystems.',
                  'You want to replace multiple subscriptions with a single tool.',
                ].map((point, i) => (
                  <XStack key={i} gap="$2" alignItems="flex-start">
                    <Text fontSize={14} color="$color7" style={{ marginTop: 2 } as any}>
                      {'\u2022'}
                    </Text>
                    <Text fontSize={14} color="$color10" lineHeight={22} flex={1}>
                      {point}
                    </Text>
                  </XStack>
                ))}
              </YStack>
            </YStack>
          </XStack>

          {/* CTA */}
          <YStack alignItems="center" gap="$4">
            <Text
              fontSize={isMobile ? 22 : 28}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-0.5}
              color="$color12"
            >
              Ready to try Torch?
            </Text>
            <Text fontSize={16} color="$color9" textAlign="center" marginBottom="$2">
              Join the waitlist and be the first to know when we launch.
            </Text>
            <SubscribeForm />
          </YStack>
        </YStack>
      </YStack>

      <Footer />
    </YStack>
  );
}
